import React from 'react';

import { MIN_USD_PRICE } from 'constants/constants';
import { LOCALIZED_PRICE_ERROR } from 'views/Offers/Offer.consts';
import { IOfferDesignOption } from 'views/Offers/hooks/useOfferDesignOptions';

import { OfferDesignSelector } from 'views/Offers/components/OfferDesignSelector';
import { SegmentSelector } from 'views/Offers/components/SegmentSelector';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import Textarea from 'components/Textarea/Textarea';

interface IDropdownOption {
  value: string;
  label: string;
}

interface ITabItem {
  value: string;
  content: string;
}

interface IGeneralSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  touched: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: string, value: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setTouched: (touched: any, shouldValidate?: boolean) => void;
  validateField: (field: string) => void;
  dup: boolean;
  isNameExists: boolean;
  isPriceLocalized: boolean;
  offerDesignOptions: IOfferDesignOption[];
  setCurrentOfferUI: (value: string) => void;
  sectionItems: IDropdownOption[];
  tabItems: ITabItem[];
  enableTabs: boolean;
  chosenSegment: string[];
  setChosenSegment: (segments: string[]) => void;
}

export const GeneralSection: React.FC<IGeneralSectionProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setTouched,
  validateField,
  dup,
  isNameExists,
  isPriceLocalized,
  offerDesignOptions,
  setCurrentOfferUI,
  sectionItems,
  tabItems,
  enableTabs,
  chosenSegment,
  setChosenSegment
}): React.ReactElement => {
  return (
    <>
      <RichText
        title="General"
        description="Start with setting up your offer mandatory fields"
        className="mb-5"
      />
      <div className="flex flex-col gap-5 mb-2">
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="Name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="Name"
            state={
              (touched.name && errors.name) || isNameExists
                ? 'error'
                : 'default'
            }
            description={
              (touched.name ? errors.name?.toString() : '') ||
              (isNameExists ? 'Name already exists' : '')
            }
            descriptionDataTestId="bundleNameValidationTxt"
          />
          <Input
            label="Display Name"
            name="displayName"
            value={values.displayName}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Display Name"
            state={
              touched.displayName && errors.displayName
                ? 'error'
                : 'default'
            }
            description={
              touched.displayName ? errors.displayName?.toString() : ''
            }
          />
        </div>
        <div>
          <Textarea
            label="Description"
            name="description"
            placeholder="Placeholder"
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="External ID"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            placeholder="ID Number"
            state={
              Boolean(errors.publisherOfferId) &&
              (touched.publisherOfferId || dup)
                ? 'error'
                : 'default'
            }
            description={
              touched.publisherOfferId || dup
                ? errors.publisherOfferId?.toString()
                : ''
            }
            descriptionDataTestId="bundleSkuValidationTxt"
          />
          <OfferDesignSelector
            options={offerDesignOptions}
            selectedValue={values.offerUiId}
            onChange={(value) => {
              setCurrentOfferUI(value);
              setFieldValue('offerUiId', value);
            }}
            listDataTestId="dashBundlesOfferDesignList"
            dataTestId="offerUiId"
            descriptionDataTestId="bundleOfferDesignValidationTxt"
            description={
              touched.offerUiId && errors.offerUiId
                ? errors.offerUiId?.toString()
                : ''
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-5">
          <InputSelectDropdown
            dataTestId="bundleSectionInput"
            label="Section"
            placeholder="Select Section"
            options={sectionItems}
            defaultSelected={
              sectionItems.find(
                (item) => item.value === values.section
              ) || undefined
            }
            onOptionChange={(value) => {
              setFieldValue('section', value);
            }}
          />
          <Input
            descriptionDataTestId="bundlePriceValidationTxt"
            label="Price"
            name="price"
            value={values.price}
            onChange={(e) => {
              setTouched({
                ...touched,
                price: true
              });
              handleChange(e);
              validateField('price');
            }}
            onBlur={handleBlur}
            isRequired
            type="number"
            min={MIN_USD_PRICE}
            leftIcon={<span>$</span>}
            leftIconStyles="text-base-foreground text-p-s absolute left-3 top-1/2 -translate-y-1/2"
            state={
              (touched.price && Boolean(errors.price)) ||
              !isPriceLocalized
                ? 'error'
                : 'default'
            }
            description={
              (touched.price ? errors.price?.toString() : '') ||
              (isPriceLocalized ? '' : LOCALIZED_PRICE_ERROR)
            }
          />
        </div>
        {enableTabs && (
          <div className="grid grid-cols-2 gap-5">
            <InputSelectDropdown
              label="Tab"
              dataTestId="bundleTabInput"
              placeholder="Select Tab"
              options={tabItems.map((item: ITabItem) => ({
                value: item.value,
                label: item.content
              }))}
              defaultSelected={
                tabItems.find(
                  (item: ITabItem) => item.value === values.tab
                )
                  ? {
                      value: values.tab,
                      label:
                        tabItems.find(
                          (item: ITabItem) => item.value === values.tab
                        )?.content || ''
                    }
                  : undefined
              }
              onOptionChange={(value) => {
                setFieldValue('tab', value);
              }}
            />
          </div>
        )}
      </div>
      <SegmentSelector
        segments={chosenSegment}
        onSegmentsChange={(newSegments) => {
          setChosenSegment(newSegments);
          setFieldValue('segments', newSegments);
        }}
        inputTestId="promoSegmentsInpt"
      />
    </>
  );
};
